import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProjectSummary = ({ formData, files, settings, message, onEdit }) => {
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const getExpirationLabel = (hours) => {
    const options = {
      '1': '1 Hour',
      '6': '6 Hours', 
      '24': '24 Hours',
      '72': '3 Days',
      '168': '1 Week'
    };
    return options?.[hours] || `${hours} Hours`;
  };

  const totalFileSize = files?.reduce((total, file) => total + file?.size, 0);
  const completedFiles = files?.filter(f => f?.status === 'completed')?.length;

  const securityFeatures = [
    { key: 'screenshotPrevention', label: 'Screenshot Prevention', enabled: settings?.screenshotPrevention },
    { key: 'watermarkProtection', label: 'Watermark Protection', enabled: settings?.watermarkProtection },
    { key: 'requireEmailVerification', label: 'Email Verification', enabled: settings?.requireEmailVerification },
    { key: 'singleUseLink', label: 'Single-Use Link', enabled: settings?.singleUseLink }
  ];

  const enabledFeatures = securityFeatures?.filter(f => f?.enabled);

  return (
    <div className="bg-card rounded-lg border border-border p-6 elevation-1">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="FileCheck" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <h3 className="text-lg font-heading text-foreground">Project Summary</h3>
          <p className="text-sm text-muted-foreground">Review your configuration before creating the secure link</p>
        </div>
      </div>
      <div className="space-y-6">
        {/* Client Information */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-foreground">Client Information</h4>
            <Button variant="ghost" size="sm" onClick={() => onEdit('client')} iconName="Edit2">
              Edit
            </Button>
          </div>
          
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Project Title:</span>
              <span className="text-sm font-medium text-foreground">{formData?.projectTitle}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Client Name:</span>
              <span className="text-sm font-medium text-foreground">{formData?.clientName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Client Email:</span>
              <span className="text-sm font-medium text-foreground">{formData?.clientEmail}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Phone Number:</span>
              <span className="text-sm font-medium text-foreground">{formData?.clientPhone}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Address:</span>
              <div className="text-right">
                <div className="text-sm font-medium text-foreground">{formData?.streetAddress}</div>
                <div className="text-sm font-medium text-foreground">
                  {formData?.city}, {formData?.state} {formData?.zipCode}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* File Summary */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-foreground">Design Assets</h4>
            <Button variant="ghost" size="sm" onClick={() => onEdit('files')} iconName="Edit2">
              Edit
            </Button>
          </div>
          
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Icon name="Files" size={16} color="var(--color-muted-foreground)" />
                <span className="text-sm font-medium text-foreground">
                  {files?.length} Files ({formatFileSize(totalFileSize)})
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                {completedFiles === files?.length ? (
                  <div className="flex items-center space-x-1 text-success">
                    <Icon name="CheckCircle" size={14} />
                    <span className="text-xs font-medium">All uploaded</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1 text-warning">
                    <Icon name="Clock" size={14} />
                    <span className="text-xs font-medium">{completedFiles}/{files?.length} uploaded</span>
                  </div>
                )}
              </div>
            </div>
            
            {files?.length > 0 && (
              <div className="space-y-1">
                {files?.slice(0, 3)?.map((file) => (
                  <div key={file?.id} className="flex items-center space-x-2 text-xs text-muted-foreground">
                    <Icon name="File" size={12} />
                    <span className="truncate flex-1">{file?.name}</span>
                    <span>{formatFileSize(file?.size)}</span>
                  </div>
                ))}
                {files?.length > 3 && (
                  <p className="text-xs text-muted-foreground">+{files?.length - 3} more files</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Security Settings */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-foreground">Security Settings</h4>
            <Button variant="ghost" size="sm" onClick={() => onEdit('security')} iconName="Edit2">
              Edit
            </Button>
          </div>
          
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Link Expiration:</span>
              <span className="text-sm font-medium text-foreground">
                {getExpirationLabel(settings?.expirationTime)}
              </span>
            </div>
            
            <div className="space-y-2">
              <span className="text-sm text-muted-foreground">Security Features:</span>
              <div className="flex flex-wrap gap-2">
                {enabledFeatures?.map((feature) => (
                  <div key={feature?.key} className="flex items-center space-x-1 px-2 py-1 bg-success/10 text-success rounded-full">
                    <Icon name="Shield" size={12} />
                    <span className="text-xs font-medium">{feature?.label}</span>
                  </div>
                ))}
                {enabledFeatures?.length === 0 && (
                  <span className="text-xs text-muted-foreground">Basic protection enabled</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Message Preview */}
        {message && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-foreground">Personal Message</h4>
              <Button variant="ghost" size="sm" onClick={() => onEdit('message')} iconName="Edit2">
                Edit
              </Button>
            </div>
            
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-sm text-foreground whitespace-pre-wrap line-clamp-4">
                {message}
              </p>
              {message?.length > 200 && (
                <Button variant="ghost" size="sm" className="mt-2 p-0 h-auto">
                  Show full message
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Estimated Timeline */}
        <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Clock" size={16} color="var(--color-accent)" />
            <span className="text-sm font-medium text-accent">Estimated Timeline</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Link generation: ~30 seconds • Email delivery: 1-2 minutes • 
            Expires: {getExpirationLabel(settings?.expirationTime)} from creation
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProjectSummary;